import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner } from "@/components/ui";
import { CircleCheck } from "lucide-react";

export type ProgressState = "loading" | "creating" | "processing" | "assignmentCompleted";

type Props = {
  progressState: ProgressState;
  onDismissAssignmentCreatedMessage: () => void;
};

export const VideoPageModalProgressOverlay: React.FC<Props> = ({
  progressState,
  onDismissAssignmentCreatedMessage,
}) => {
  const [autoDismissSeconds, setAutoDismissSeconds] = useState(0);
  const autoDismissIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearAutoDismissTimers = useCallback(() => {
    setAutoDismissSeconds(0);
    if (autoDismissIntervalRef.current != null) {
      clearInterval(autoDismissIntervalRef.current);
      autoDismissIntervalRef.current = null;
    }
  }, []);

  const handleDismiss = useCallback(() => {
    clearAutoDismissTimers();
    onDismissAssignmentCreatedMessage();
  }, [clearAutoDismissTimers, onDismissAssignmentCreatedMessage]);

  useEffect(() => {
    if (progressState !== "assignmentCompleted") return;

    let seconds = 3;
    setAutoDismissSeconds(seconds);
    autoDismissIntervalRef.current = setInterval(() => {
      seconds--;
      setAutoDismissSeconds(seconds);
      if (seconds <= 0) {
        handleDismiss();
      }
    }, 1000);

    return () => {
      clearAutoDismissTimers();
    };
  }, [progressState, handleDismiss]);

  const message = useMemo(() => {
    switch (progressState) {
      case "creating":
        return "Creating your page...";
      case "processing":
        return "Finishing things up...";
      case "assignmentCompleted":
        return "Assignment complete";
      case "loading":
        return "Loading...";
    }
  }, [progressState]);

  const icon = useMemo(() => {
    switch (progressState) {
      case "creating":
      case "processing":
        return <Spinner className="size-10 shrink-0" />;
      case "assignmentCompleted":
        return <CircleCheck className="size-14 text-green-600 shrink-0" />;
      case "loading":
        return <Spinner className="size-10 shrink-0" />;
    }
  }, [progressState]);

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 px-6 max-w-lg mx-auto text-center">
      {icon}
      <div className="flex flex-col gap-2">
        <span className="text-lg font-semibold">{message}</span>
        {progressState === "assignmentCompleted" && (
          <Button variant="link" size="sm" onClick={handleDismiss}>
            Dismiss{autoDismissSeconds > 0 && ` (${autoDismissSeconds})`}
          </Button>
        )}
      </div>
    </div>
  );
};
